export const LANE_COLORS = [
  '#0d6efd',
  '#198754',
  '#fd7e14',
  '#6f42c1',
  '#d63384',
  '#20c997',
  '#dc3545',
  '#0dcaf0',
];

export const toDayKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const completionFromTask = (task, completedAt = new Date()) => ({
  taskId: task.taskId,
  completedAt: completedAt.toISOString(),
});

export const laneName = (stack, index) => {
  const title = stack && stack.title ? String(stack.title).trim() : '';
  return title || `Lane ${index + 1}`;
};

export const taskHours = (task) => {
  if (!task || !task.seriesCount) {
    return 1;
  }
  const hours = parseInt(task.seriesCount, 10);
  return Number.isNaN(hours) ? 1 : hours;
};

export const formatHours = (hours) => {
  if (hours > 8) {
    return Math.round(hours / 8) + 'd';
  }
  return hours + 'h';
};

const tasksForFilter = (tasks, filter) => {
  if (!filter) {
    return tasks || [];
  }
  return (tasks || []).filter((task) => (task.tags || '').includes(filter));
};

const percentsThatSumTo100 = (hours) => {
  const total = hours.reduce((sum, value) => sum + value, 0);
  if (total === 0) {
    return hours.map(() => 0);
  }
  const exact = hours.map((value) => (value / total) * 100);
  const percents = exact.map((value) => Math.floor(value));
  let leftover = 100 - percents.reduce((sum, value) => sum + value, 0);
  const order = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);
  for (let i = 0; i < leftover; i += 1) {
    percents[order[i].index] += 1;
  }
  return percents;
};

/**
 * Each lane's share of the hours still open, so lanes can be compared as percentages.
 */
export const buildRemainingComparison = (stacks, options = {}) => {
  const filter = options.filter || '';
  const rows = (stacks || []).map((stack, index) => {
    const leftHours = tasksForFilter(stack.tasks, filter).reduce((sum, task) => sum + taskHours(task), 0);
    return {
      id: stack.id,
      name: laneName(stack, index),
      color: LANE_COLORS[index % LANE_COLORS.length],
      leftHours,
    };
  });
  const percents = percentsThatSumTo100(rows.map((row) => row.leftHours));
  const totalLeft = rows.reduce((sum, row) => sum + row.leftHours, 0);
  return {
    totalLeft,
    rows: rows.map((row, index) => ({
      ...row,
      percentLeft: percents[index],
      timeleft: formatHours(row.leftHours),
    })),
  };
};

const startOfLocalDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const formatDayLabel = (date) => `${date.getMonth() + 1}/${date.getDate()}`;

/**
 * Tasks completed per day for each lane, over a window ending on `now`.
 */
export const buildVelocityChart = (stacks, options = {}) => {
  const now = options.now instanceof Date ? options.now : new Date();
  const dayCount = options.dayCount || 14;
  const today = startOfLocalDay(now);
  const days = [];
  for (let offset = dayCount - 1; offset >= 0; offset -= 1) {
    const date = new Date(today.getTime());
    date.setDate(today.getDate() - offset);
    days.push({
      key: toDayKey(date),
      label: formatDayLabel(date),
    });
  }

  const dayKeys = new Set(days.map((day) => day.key));
  const lanes = (stacks || []).map((stack, index) => ({
    id: stack.id,
    name: laneName(stack, index),
    color: LANE_COLORS[index % LANE_COLORS.length],
  }));

  const counts = {};
  days.forEach((day) => {
    counts[day.key] = {};
    lanes.forEach((lane) => {
      counts[day.key][lane.id] = 0;
    });
  });

  (stacks || []).forEach((stack) => {
    (stack.completions || []).forEach((completion) => {
      const key = toDayKey(completion.completedAt);
      if (!key || !dayKeys.has(key) || counts[key][stack.id] == null) {
        return;
      }
      counts[key][stack.id] += 1;
    });
  });

  let max = 0;
  let total = 0;
  days.forEach((day) => {
    lanes.forEach((lane) => {
      const count = counts[day.key][lane.id];
      max = Math.max(max, count);
      total += count;
    });
  });

  const laneTotals = {};
  lanes.forEach((lane) => {
    laneTotals[lane.id] = days.reduce((sum, day) => sum + counts[day.key][lane.id], 0);
  });

  return { days, lanes, counts, max, total, laneTotals };
};
